import React, { useState, useEffect } from "react";
import { phappsRequest } from "@/services/apiService";
import { API_PATHS } from "@/config/apiConstants";

const emailOk = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(s).trim());

/** Card toggle — used for Internal access (PH Admin, Zephyr). */
function InternalRoleCard({ label, color, checked, onChange, disabled }) {
  return (
    <button
      type="button"
      onClick={() => !disabled && onChange(!checked)}
      disabled={disabled}
      className={`flex-1 text-left rounded-xl border-2 p-3 transition-all ${
        checked ? "shadow-sm" : "border-slate-200 bg-white hover:border-slate-300"
      }`}
      style={checked ? { borderColor: color, backgroundColor: `${color}12` } : {}}
    >
      <div className="flex items-center gap-2.5">
        <div
          className="w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 transition-colors"
          style={
            checked
              ? { borderColor: color, backgroundColor: color }
              : { borderColor: "#cbd5e1", backgroundColor: "transparent" }
          }
        >
          {checked && (
            <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 12 12">
              <path d="M2 6l3 3 5-5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          )}
        </div>
        <p className="text-sm font-semibold" style={checked ? { color } : { color: "#475569" }}>
          {label}
        </p>
      </div>
    </button>
  );
}

/** Pill toggle — used for External portals. Rounded-full track, circle thumb. */
function PillToggleRow({ label, checked, onChange, disabled }) {
  return (
    <div
      className={`flex items-center justify-between px-3 py-2.5 border-b border-slate-100 last:border-b-0 select-none transition-colors ${
        disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer hover:bg-slate-50"
      }`}
      onClick={() => !disabled && onChange(!checked)}
    >
      <span className="text-sm font-medium text-slate-700">{label}</span>
      <div
        className="relative flex-shrink-0 rounded-full transition-colors duration-200"
        style={{ width: 40, height: 24, backgroundColor: checked ? "#16a34a" : "#dc2626" }}
      >
        <div
          className="absolute bg-white rounded-full shadow transition-transform duration-200"
          style={{ width: 16, height: 16, top: 4, transform: checked ? "translateX(20px)" : "translateX(4px)" }}
        />
      </div>
    </div>
  );
}

// ── Role definitions ─────────────────────────────────────────────────────────

const INTERNAL_ROLES = [
  { key: "IsPHAdmin",              label: "PH Admin",    color: "#2563eb" },
  { key: "IsZephyrAdmin",          label: "Zephyr Admin", color: "#38184f" },
  { key: "is_conferences_user",    label: "Conferences", color: "#FFAF3C" },
];

// Add new external portals here — appear automatically in the list
const EXTERNAL_ROLES = [
  { key: "IsHubMedAdmin",  label: "HubMed Admin"  },
  { key: "IsHolaAdmin",    label: "Hola Admin"    },
  { key: "IsMedmateAdmin", label: "Medmate Admin" },
];

const ALL_ROLE_KEYS = [...INTERNAL_ROLES, ...EXTERNAL_ROLES].map(r => r.key);
const EMPTY_ROLES   = Object.fromEntries(ALL_ROLE_KEYS.map(k => [k, false]));

// ── Component ────────────────────────────────────────────────────────────────

const CreateGroupManagerModal = ({ isOpen, onClose, onCreated }) => {
  const [email, setEmail]       = useState("");
  const [firstName, setFirstName] = useState("");
  const [roles, setRoles]       = useState(EMPTY_ROLES);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState(null);

  useEffect(() => {
    if (!isOpen) return;
    setEmail("");
    setFirstName("");
    setRoles(EMPTY_ROLES);
    setSaving(false);
    setError(null);
  }, [isOpen]);

  if (!isOpen) return null;

  const toggle = (key) => (val) => setRoles(r => ({ ...r, [key]: val }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    const em = email.trim();
    const fn = firstName.trim();
    if (!emailOk(em)) { setError("Enter a valid email address."); return; }
    if (!fn) { setError("First name is required."); return; }
    setSaving(true);
    try {
      const body = { Email: em, FirstName: fn, ...roles };
      const res  = await phappsRequest(API_PATHS.GROUP_MANAGERS_CREATE, "POST", body);
      onCreated?.(res);
      onClose();
    } catch (err) {
      setError(err.message || "Create failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 overflow-y-auto p-4">
      <div className="bg-white rounded-2xl w-full max-w-md mx-auto my-8 shadow-xl">

        {/* Header */}
        <div className="px-6 py-5 border-b border-slate-100">
          <h2 className="text-xl font-bold text-slate-900">Create manager</h2>
          <p className="text-sm text-slate-500 mt-0.5">An invitation will be sent to the email address.</p>
        </div>

        <form onSubmit={handleSubmit} className="px-6 py-5 space-y-5">

          {/* Name + email */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">Email address</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                placeholder="name@email.com"
                required
                disabled={saving}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5">First name</label>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                className="w-full rounded-lg border border-slate-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-400"
                placeholder="Jane"
                required
                disabled={saving}
              />
            </div>
          </div>

          {/* Internal — PH Admin + Zephyr */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">Internal access</p>
            <div className="flex gap-2">
              {INTERNAL_ROLES.map(r => (
                <InternalRoleCard
                  key={r.key}
                  label={r.label}
                  color={r.color}
                  checked={roles[r.key]}
                  onChange={toggle(r.key)}
                  disabled={saving}
                />
              ))}
            </div>
          </div>

          {/* External portals — scalable list */}
          <div>
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wider mb-2">External portals</p>
            <div className="rounded-xl border border-slate-200 overflow-hidden">
              {EXTERNAL_ROLES.map(r => (
                <PillToggleRow
                  key={r.key}
                  label={r.label}
                  checked={roles[r.key]}
                  onChange={toggle(r.key)}
                  disabled={saving}
                />
              ))}
            </div>
          </div>

          {error && (
            <div className="rounded-lg bg-red-50 text-red-700 text-sm px-3 py-2 border border-red-100">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-1">
            <button
              type="button"
              onClick={onClose}
              disabled={saving}
              className="px-4 py-2 text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 text-sm font-medium"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={saving}
              className="px-5 py-2 text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-semibold"
            >
              {saving ? "Creating…" : "Create manager"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateGroupManagerModal;
